"use client";

import { useEffect, useRef, useState } from "react";
import { useTurnstile } from "@/hooks/useTurnstile";
import { useCoAnalystSignup } from "./useCoAnalystSignup";

const DISMISS_KEY = "coanalyst-banner-dismissed";
const ENTER_DELAY_MS = 600;
const EXIT_MS = 350;

/**
 * Fixed bottom free-trial bar for phones (`md:hidden`), where the sidebar
 * access card sits far below the fold and rarely gets seen.
 *
 * Same email-only capture as `CoAnalystAccessCard` (shared
 * `useCoAnalystSignup` hook, same `/api/leads/trial` endpoint) with a distinct
 * `useCase` so mobile-bar leads are attributable in Copper.
 *
 * Visually it belongs to the site's dark CTA family (`#0a0a0a`, display face),
 * as a bar rather than a card. It slides up shortly after load and
 * slides back down on dismiss; the global `prefers-reduced-motion` rule in
 * globals.css neutralises both transitions.
 *
 * The Turnstile widget only mounts once the visitor engages with the email
 * field — it keeps the bar short and avoids loading the challenge for every
 * mobile pageview. It sits above the input row, which grows the bar upward and
 * leaves the CTA where the thumb already is.
 *
 * Must be rendered outside any framer-motion wrapper: a transformed ancestor
 * would make `position: fixed` anchor to the wrapper instead of the viewport.
 *
 * While mounted it publishes its own height as `--mobile-cta-h` and pads the
 * body by the same amount, so the page bottom and other fixed UI (see
 * `ScrollToTop`) clear the bar. The value is 0 above `md` — where the bar is
 * `display: none` — and on dismissal.
 */
export default function MobileCoAnalystBanner() {
  const barRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [shown, setShown] = useState(false);
  const [engaged, setEngaged] = useState(false);
  const { containerRef, token, reset } = useTurnstile({ theme: "dark", active: engaged });
  const { email, setEmail, submitting, redirecting, error, fieldError, handleSubmit } =
    useCoAnalystSignup({
      useCase: "Co-Analyst Access (mobile banner)",
      token,
      resetTurnstile: reset,
    });

  // Client-only: dismissal is per-session, and rendering nothing on the server
  // keeps the markup consistent between SSR and hydration.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
    } catch {
      // Private mode / blocked storage — show the bar anyway.
    }
    setVisible(true);
    const timer = setTimeout(() => setShown(true), ENTER_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // Publish the bar's height so surrounding UI can clear it. Re-measured on
  // resize and whenever the bar grows (e.g. the Turnstile widget mounts).
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;

    const apply = () => {
      // 0 while `md:hidden` applies — display:none elements have no offset height.
      const h = `${el.offsetHeight}px`;
      document.documentElement.style.setProperty("--mobile-cta-h", h);
      document.body.style.paddingBottom = h;
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    window.addEventListener("resize", apply);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", apply);
      document.documentElement.style.removeProperty("--mobile-cta-h");
      document.body.style.removeProperty("padding-bottom");
    };
  }, [visible]);

  const dismiss = () => {
    setShown(false);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Dismissal just won't persist across pages.
    }
    // Unmount after the slide-down, so the height var and body padding are
    // released once the bar is actually gone.
    setTimeout(() => setVisible(false), EXIT_MS);
  };

  if (!visible) return null;

  return (
    <div
      ref={barRef}
      className={`md:hidden fixed inset-x-0 bottom-0 z-40 overflow-hidden rounded-t-2xl bg-[#0a0a0a] text-white shadow-[0_-10px_30px_rgba(0,0,0,0.35)] transition-transform duration-[400ms] ease-[cubic-bezier(0.16,1,0.3,1)] px-4 pt-3.5 pb-[calc(0.875rem+env(safe-area-inset-bottom))] ${
        shown ? "translate-y-0" : "translate-y-full"
      }`}
      role="region"
      aria-label="Try Hudson Labs free"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        /* z-20: above the content wrapper, whose full-width headline box would
           otherwise swallow taps in this corner. */
        className="absolute top-1.5 right-1.5 z-20 p-3 text-white/40 hover:text-white transition-colors"
      >
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          aria-hidden="true"
        >
          <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      <div className="relative z-10">
        {redirecting ? (
          <p className="text-sm text-white/80 py-3 pr-10">
            You&apos;re all set. Taking you to sign up&hellip;
          </p>
        ) : (
          <>
            <p className="pr-10 text-[17px] leading-tight font-[family-name:var(--font-display)] tracking-[-0.02em]">
              Try Hudson Labs free for 14 days.
            </p>
            <p className="mt-1 mb-2.5 text-[11px] text-white/45">
              No charge for 14 days. Then{" "}
              <span className="font-semibold text-white">$119/month. Cancel anytime.</span>
            </p>

            <form onSubmit={handleSubmit}>
              {(error || fieldError) && (
                <p className="mb-2 text-xs text-red-300" role="alert">
                  {error || fieldError}
                </p>
              )}

              {/* Turnstile sits above the input row: mounting it grows the bar
                  upward and leaves the CTA under the same thumb position. */}
              {engaged && <div ref={containerRef} className="mb-2" />}

              <div className="flex items-center gap-2">
                <input
                  type="email"
                  placeholder="name@company.com"
                  required
                  disabled={submitting}
                  autoComplete="email"
                  value={email}
                  onFocus={() => setEngaged(true)}
                  onChange={(e) => {
                    setEngaged(true);
                    setEmail(e.target.value);
                  }}
                  className={`min-w-0 flex-1 rounded-xl bg-white/[0.07] border px-3.5 py-3 text-[15px] text-white placeholder:text-white/35 outline-none transition-colors ${
                    fieldError
                      ? "border-red-400/70 focus:border-red-400"
                      : "border-white/15 focus:border-violet-500"
                  }`}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="shrink-0 rounded-xl bg-violet-600 hover:bg-violet-700 active:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-3 text-[13px] font-medium text-white whitespace-nowrap transition-colors"
                >
                  {submitting ? "Sending…" : "Start free trial"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
